import { createStyles } from '@/hooks';
import React, { useEffect } from 'react';
import {
  BackHandler,
  Modal as RNModal,
  Platform,
  Pressable,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import Animated, {
  type EntryOrExitLayoutType,
  FadeIn,
  FadeOut,
  ZoomIn,
  ZoomOut,
} from 'react-native-reanimated';

export type ModalProps = {
  visible: boolean;
  children: React.ReactNode;
  entering?: EntryOrExitLayoutType;
  exiting?: EntryOrExitLayoutType;
  onModalHide?: () => void;
  onBackButtonPress?: () => void;
  onBackdropPress?: () => void;
  /**
   * Custom container style for the centered content.
   */
  contentContainerStyle?: StyleProp<ViewStyle>;
};

const Modal: React.FC<ModalProps> = ({
  visible,
  children,
  entering = ZoomIn,
  exiting = ZoomOut,
  onModalHide,
  onBackButtonPress,
  onBackdropPress,
  contentContainerStyle,
}) => {
  const styles = useStyles();
  useEffect(() => {
    if (!visible) {
      onModalHide?.();
    }
  }, [visible, onModalHide]);

  useEffect(() => {
    const backPressHandler = BackHandler.addEventListener('hardwareBackPress', () => {
      if (onBackButtonPress && visible) {
        onBackButtonPress?.();
        return true;
      }
      return false;
    });
    return () => {
      backPressHandler.remove();
    };
  }, [onBackButtonPress, visible]);

  if (!visible) {
    return null;
  }

  return (
    <RNModal
      transparent
      visible={visible}
      animationType="none"
      onRequestClose={onBackButtonPress}
      supportedOrientations={['portrait', 'landscape']}
      statusBarTranslucent
      navigationBarTranslucent={Platform.OS === 'android'}>
      <Animated.View entering={FadeIn} exiting={FadeOut} style={styles.backdrop} />

      <Pressable style={styles.backdropOverlay} onPress={onBackdropPress}>
        {/* capture backdrop taps */}
      </Pressable>

      <View style={[styles.container, contentContainerStyle]}>
        <Animated.View entering={entering} exiting={exiting}>
          {children}
        </Animated.View>
      </View>
    </RNModal>
  );
};

const useStyles = createStyles(({ deviceWidth, deviceHeight }) => ({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: deviceWidth,
    height: deviceHeight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  backdropOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
}));

export default Modal;
